'use client';

import { Skeleton } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBootstrapAuth } from '@/features/auth/hooks';
import { useContextTabDirtyState } from '@/features/context-tabs';
import { usePatchUserByIdMutation } from '@/features/users/api/usersApi';
import { profileInformationSchema } from '@/features/users/schemas';
import { useRequestStorageUploadMutation } from '@/features/workspaces/api/workspacesApi';
import { buildStorageViewUrl } from '@/utils/storage';
import { useAssetUpload } from '@web/hooks';
import { Avatar, MainButton, SecondaryButton, SmartInput } from '@web/ui';
import {
  backendErrorsToFieldErrors,
  getErrorMessage,
  notify,
  zodErrorsToFieldErrors,
} from '@web/utils';

import styles from './ProfilePanel.module.scss';

import type {
  CurrentUserContextResponse,
  PatchUserPayload,
  UserProfile,
} from '@/features/users/types';

type ProfileInformationSectionProps = {
  userProfile: UserProfile | null;
  currentUserContext: CurrentUserContextResponse | null;
  isLoading?: boolean;
};

type ProfileInformationForm = {
  first_name: string;
  last_name: string;
  avatar_asset_key: string;
};

type ProfileInformationField = keyof ProfileInformationForm;
type ProfileInformationErrors = Partial<
  Record<ProfileInformationField, string>
>;

type UserAvatarSource = {
  avatar_asset_key?: string | null;
  avatar_url?: string | null;
  avatar?: string | null;
};

function resolveAvatarSrc(user: UserAvatarSource | null | undefined): string {
  if (!user) {
    return '';
  }

  return (
    buildStorageViewUrl(user.avatar_asset_key) ||
    user.avatar_url ||
    user.avatar ||
    ''
  );
}

function normalizeForm(values: ProfileInformationForm): ProfileInformationForm {
  return {
    first_name: values.first_name.trim(),
    last_name: values.last_name.trim(),
    avatar_asset_key: (values.avatar_asset_key || '').trim(),
  };
}

function mapBackendErrorsToFormErrors(
  backendErrors: unknown,
): ProfileInformationErrors {
  const flatErrors = backendErrorsToFieldErrors(backendErrors);
  const formErrors: ProfileInformationErrors = {};
  const acceptedKeys: ProfileInformationField[] = [
    'first_name',
    'last_name',
    'avatar_asset_key',
  ];

  acceptedKeys.forEach((key) => {
    if (flatErrors[key]) {
      formErrors[key] = flatErrors[key];
    }
  });

  return formErrors;
}

function extractApiErrorDetails(error: unknown): {
  message?: string;
  errors?: unknown;
} {
  if (
    typeof error === 'object' &&
    error !== null &&
    'data' in error &&
    typeof (error as { data?: unknown }).data === 'object' &&
    (error as { data?: unknown }).data !== null
  ) {
    const data = (error as { data: Record<string, unknown> }).data;
    return {
      message: typeof data.message === 'string' ? data.message : undefined,
      errors: data.errors,
    };
  }

  return {};
}

export function ProfileInformationSection({
  userProfile,
  currentUserContext,
  isLoading = false,
}: ProfileInformationSectionProps) {
  const bootstrapAuth = useBootstrapAuth();
  const [patchUserById, { isLoading: isSaving }] = usePatchUserByIdMutation();
  const [requestStorageUpload] = useRequestStorageUploadMutation();

  const resolvedAvatarAssetKey = userProfile?.avatar_asset_key || '';
  const initialForm = normalizeForm({
    first_name: userProfile?.first_name || '',
    last_name: userProfile?.last_name || '',
    avatar_asset_key: resolvedAvatarAssetKey,
  });

  const [form, setForm] = useState<ProfileInformationForm>(initialForm);
  const [initialFormSnapshot, setInitialFormSnapshot] =
    useState<ProfileInformationForm>(initialForm);
  const [errors, setErrors] = useState<ProfileInformationErrors>({});
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(
    resolveAvatarSrc(userProfile) || resolveAvatarSrc(currentUserContext?.user),
  );
  const [isRemovingPhoto, setIsRemovingPhoto] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const blobPreviewUrlRef = useRef<string | null>(null);

  const requestUpload = useCallback(
    async (payload: {
      asset_kind: string;
      filename: string;
      content_type?: string;
    }) => {
      const response = await requestStorageUpload(payload).unwrap();

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Unable to prepare upload.');
      }

      return response.data;
    },
    [requestStorageUpload],
  );

  const { uploadAsset, isUploading } = useAssetUpload({ requestUpload });

  const setPreview = useCallback((nextUrl: string) => {
    if (blobPreviewUrlRef.current) {
      URL.revokeObjectURL(blobPreviewUrlRef.current);
      blobPreviewUrlRef.current = null;
    }

    if (nextUrl.startsWith('blob:')) {
      blobPreviewUrlRef.current = nextUrl;
    }

    setAvatarPreviewUrl(nextUrl);
  }, []);

  useEffect(() => {
    return () => {
      if (blobPreviewUrlRef.current) {
        URL.revokeObjectURL(blobPreviewUrlRef.current);
        blobPreviewUrlRef.current = null;
      }
    };
  }, []);

  const isDirty = useMemo(() => {
    const normalizedCurrent = normalizeForm(form);
    const normalizedInitial = normalizeForm(initialFormSnapshot);

    return (
      normalizedCurrent.first_name !== normalizedInitial.first_name ||
      normalizedCurrent.last_name !== normalizedInitial.last_name ||
      normalizedCurrent.avatar_asset_key !== normalizedInitial.avatar_asset_key
    );
  }, [form, initialFormSnapshot]);
  useContextTabDirtyState(isDirty);

  const hasPhoto = Boolean(form.avatar_asset_key || avatarPreviewUrl);
  const isBusy = isSaving || isUploading || isRemovingPhoto;

  const syncCurrentUserInStore = useCallback(
    (nextUser: UserProfile | null | undefined) => {
      if (!nextUser) {
        return;
      }

      bootstrapAuth({
        currentUser: nextUser as any,
        currentWorkspaceSlug: currentUserContext?.workspace?.slug ?? null,
        isWorkspaceSetupRequired:
          currentUserContext?.is_workspace_setup_required ?? null,
        isWorkspaceOnboarded:
          currentUserContext?.workspace?.is_workspace_onboarded ?? null,
      });
    },
    [bootstrapAuth, currentUserContext],
  );

  async function handleSave(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!userProfile?.id || !isDirty) {
      return;
    }

    setErrors({});

    const parsedForm = profileInformationSchema.safeParse(form);
    if (!parsedForm.success) {
      setErrors(
        zodErrorsToFieldErrors<ProfileInformationForm>(parsedForm.error),
      );
      return;
    }

    const normalizedAvatarAssetKey = (
      parsedForm.data.avatar_asset_key || ''
    ).trim();
    const payload: PatchUserPayload = {
      first_name: parsedForm.data.first_name.trim(),
      last_name: parsedForm.data.last_name.trim(),
      avatar_asset_key: normalizedAvatarAssetKey || null,
    };

    try {
      const response = await patchUserById({
        userId: userProfile.id,
        payload,
      }).unwrap();

      if (!response.success) {
        const fieldErrors = mapBackendErrorsToFormErrors(response.errors);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((previous) => ({ ...previous, ...fieldErrors }));
        }
        notify({
          type: 'error',
          message: response.message || 'Unable to update profile.',
        });
        return;
      }

      const persistedUser = response.data || userProfile;
      const persistedForm = normalizeForm({
        first_name: persistedUser.first_name || parsedForm.data.first_name,
        last_name: persistedUser.last_name || parsedForm.data.last_name,
        avatar_asset_key:
          persistedUser.avatar_asset_key || normalizedAvatarAssetKey,
      });

      setForm(persistedForm);
      setInitialFormSnapshot(persistedForm);
      setPreview(resolveAvatarSrc(persistedUser));
      syncCurrentUserInStore(persistedUser);

      notify({
        type: 'success',
        message: response.message || 'Profile updated successfully.',
      });
    } catch (error) {
      const errorDetails = extractApiErrorDetails(error);
      const fieldErrors = mapBackendErrorsToFormErrors(errorDetails.errors);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((previous) => ({ ...previous, ...fieldErrors }));
      }

      notify({
        type: 'error',
        message: errorDetails.message || getErrorMessage(error),
      });
    }
  }

  const handlePhotoFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';

      if (!file) {
        return;
      }

      if (!file.type.startsWith('image/')) {
        notify({
          type: 'error',
          message: 'Please choose an image file for your profile photo.',
        });
        return;
      }

      try {
        const assetKey = await uploadAsset({
          file,
          assetKind: 'avatar',
        });
        const previewUrl = URL.createObjectURL(file);

        setForm((previous) => ({
          ...previous,
          avatar_asset_key: assetKey,
        }));
        setPreview(previewUrl);
        notify({ type: 'success', message: 'Photo ready to save.' });
      } catch (error) {
        notify({ type: 'error', message: getErrorMessage(error) });
      }
    },
    [setPreview, uploadAsset],
  );

  const handleRemovePhoto = useCallback(async () => {
    if (!userProfile?.id || !hasPhoto || isBusy) {
      return;
    }

    setIsRemovingPhoto(true);
    setErrors((previous) => ({
      ...previous,
      avatar_asset_key: undefined,
    }));

    try {
      const response = await patchUserById({
        userId: userProfile.id,
        payload: { avatar_asset_key: null },
      }).unwrap();

      if (!response.success) {
        const fieldErrors = mapBackendErrorsToFormErrors(response.errors);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((previous) => ({ ...previous, ...fieldErrors }));
        }
        notify({
          type: 'error',
          message: response.message || 'Unable to remove photo.',
        });
        return;
      }

      const persistedUser = response.data || userProfile;
      const nextForm = {
        ...form,
        avatar_asset_key: '',
      };

      setForm(nextForm);
      setInitialFormSnapshot((previous) => ({
        ...previous,
        avatar_asset_key: '',
      }));
      setPreview('');
      syncCurrentUserInStore({
        ...persistedUser,
        avatar_asset_key: null,
        avatar_url: '',
        avatar: '',
      });
      notify({
        type: 'success',
        message: response.message || 'Profile photo removed.',
      });
    } catch (error) {
      const errorDetails = extractApiErrorDetails(error);
      const fieldErrors = mapBackendErrorsToFormErrors(errorDetails.errors);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((previous) => ({ ...previous, ...fieldErrors }));
      }

      notify({
        type: 'error',
        message: errorDetails.message || getErrorMessage(error),
      });
    } finally {
      setIsRemovingPhoto(false);
    }
  }, [
    form,
    hasPhoto,
    isBusy,
    patchUserById,
    setPreview,
    syncCurrentUserInStore,
    userProfile,
  ]);

  if (isLoading) {
    return (
      <section className={`card-container-glass ${styles.card}`}>
        <header className={styles.cardHeader}>
          <Skeleton variant="rounded" width={180} height={30} />
          <Skeleton variant="rounded" width={260} height={18} />
        </header>

        <div className={styles.loadingPhotoRow}>
          <Skeleton variant="circular" width={64} height={64} />
          <Skeleton variant="rounded" width={120} height={36} />
          <Skeleton variant="rounded" width={120} height={36} />
        </div>

        <Skeleton variant="rounded" width="100%" height={18} />

        <div className={styles.nameRow}>
          <Skeleton variant="rounded" width="100%" height={56} />
          <Skeleton variant="rounded" width="100%" height={56} />
        </div>

        <div className={styles.emailBlock}>
          <Skeleton variant="rounded" width={80} height={16} />
          <Skeleton variant="rounded" width="100%" height={48} />
        </div>

        <div className={styles.loadingActionsRow}>
          <Skeleton variant="rounded" width={120} height={40} />
        </div>
      </section>
    );
  }

  if (!userProfile) {
    return (
      <section className={`card-container-glass ${styles.card}`}>
        <header className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Profile information</h2>
          <p className={styles.cardDescription}>
            We could not load your profile information right now.
          </p>
        </header>
      </section>
    );
  }

  return (
    <section className={`card-container-glass ${styles.card}`}>
      <header className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>Profile information</h2>
        <p className={styles.cardDescription}>Update your name and photo.</p>
      </header>

      <form className={styles.form} onSubmit={handleSave}>
        <div className={styles.photoRow}>
          <Avatar
            name={`${form.first_name} ${form.last_name}`.trim() || 'User'}
            src={avatarPreviewUrl}
            size="xLarge"
          />

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className={styles.hiddenFileInput}
            onChange={handlePhotoFileSelect}
          />

          <MainButton
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isBusy}
            ariaLabel="Upload profile photo"
          >
            {isUploading ? 'Uploading photo...' : 'Upload photo'}
          </MainButton>

          <SecondaryButton
            ariaLabel="Remove profile photo"
            onClick={() => {
              void handleRemovePhoto();
            }}
          >
            {isRemovingPhoto ? 'Removing photo...' : 'Remove photo'}
          </SecondaryButton>
        </div>

        <p className={styles.photoHint}>
          This photo is used across Net Net. Workspace admins will see it in
          team settings.
        </p>

        <div className={styles.nameRow}>
          <SmartInput
            type="text"
            name="profile_first_name"
            label="First name"
            value={form.first_name}
            onChange={(value) => {
              setForm((previous) => ({ ...previous, first_name: value }));
              if (errors.first_name) {
                setErrors((previous) => ({
                  ...previous,
                  first_name: undefined,
                }));
              }
            }}
            onValidateChange={(_, error) =>
              setErrors((previous) => ({
                ...previous,
                first_name: error || undefined,
              }))
            }
            schema={profileInformationSchema.shape.first_name}
            error={errors.first_name}
          />

          <SmartInput
            type="text"
            name="profile_last_name"
            label="Last name"
            value={form.last_name}
            onChange={(value) => {
              setForm((previous) => ({ ...previous, last_name: value }));
              if (errors.last_name) {
                setErrors((previous) => ({
                  ...previous,
                  last_name: undefined,
                }));
              }
            }}
            onValidateChange={(_, error) =>
              setErrors((previous) => ({
                ...previous,
                last_name: error || undefined,
              }))
            }
            schema={profileInformationSchema.shape.last_name}
            error={errors.last_name}
          />
        </div>

        <div className={styles.emailBlock}>
          <p className={styles.emailLabel}>Email</p>
          <p className={styles.emailValue}>{userProfile.email}</p>
        </div>

        <div className={styles.actions}>
          <MainButton
            type="submit"
            isLoading={isSaving}
            disabled={!isDirty || isBusy}
          >
            Save changes
          </MainButton>
        </div>
      </form>
    </section>
  );
}
